import { useState, MutableRefObject } from "react";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import TransferAmountDialog from "./TransferAmountDialog";
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import cloneDeep from "lodash-es/cloneDeep";
import { editTransferRequest } from "@/DataPages/api/inventory/mutations";
import RowFeedback from "@/Common/helpers/RowFeedback";
import { IVehicle } from "../../types/dealerTrades";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";

type TTransferAmountCellProps = {
	params: GridRenderCellParams;
	locationId: string;
	commissionCycleId?: string;
	payPeriodId: string;
	disableOpenDialog?: boolean;
	table: string;
	section: "pendingRequest" | "transferRequest";
	apiRef?: MutableRefObject<GridApiPremium>;
};

const allowedStatus = ["Pending Approval", "Pending Arrival and Approval"];

const updateTransferAmount = (
	data: { pages: { edges: { node: { _id: string; transferAmount: number } }[] }[] },
	id: string,
	transferAmount: number
) => {
	for (const page of data.pages) {
		for (const edge of page.edges) {
			if (edge.node._id === id) {
				edge.node.transferAmount = transferAmount;
				break; // Stop once the node is updated
			}
		}
	}
	return data;
};

const TransferAmountCell = (props: TTransferAmountCellProps) => {
	const { params, locationId, commissionCycleId, payPeriodId, section, apiRef, disableOpenDialog, table } = props;
	const { _id: rowId, requestId, vehicle } = params.row;
	const isPendingRequest = section === "pendingRequest";
	const [isOpen, setIsOpen] = useState(false);
	const queryClient = useQueryClient();
	const canOpenDialog = allowedStatus.includes(params.row?.requestStatus?.status ?? "Pending Approval");

	const queryKey = isPendingRequest
		? ["inventory", "pendingRequest", locationId, commissionCycleId, payPeriodId]
		: ["getInventoryTradeVehicles", locationId, table];

	const { mutate } = useMutation({
		mutationKey: ["editTransferRequest", locationId],
		mutationFn: async ({ _id, requestId: resId, input }: { _id: string; requestId: string; input: IVehicle }) => {
			return editTransferRequest({ input, _id, locationId, requestId: resId });
		},
		onSuccess: () => {
			RowFeedback.skeleton(rowId, apiRef)?.success();
		},
		onError: () => {
			RowFeedback.skeleton(rowId, apiRef)?.success();
		},
	});

	const onCloseDialog = () => {
		setIsOpen(false);
	};

	const onConfirm = (internalValue: number) => {
		if (isPendingRequest) {
			const newVehicle = { ...vehicle, transferAmount: internalValue };
			RowFeedback.skeleton(rowId, apiRef)?.display();
			mutate({ _id: rowId, input: newVehicle, requestId });
		} else {
			queryClient.setQueryData(queryKey, (oldData: { pages: { edges: { node: { _id: string; transferAmount: number } }[] }[] }) => {
				return updateTransferAmount(cloneDeep(oldData), rowId, internalValue);
			});
		}
	};

	return (
		<>
			<TypographyCell
				render="absolute"
				params={params}
				hints={{ isMoney: true, noFocus: true, noWrap: true }}
				className={clsx("!text-primary !decoration-primary !hover:cursor-pointer", {
					"!underline": params.value,
				})}
				onClick={() => setIsOpen(true)}
			/>
			<TransferAmountDialog
				isOpen={isOpen && canOpenDialog && !disableOpenDialog}
				onCloseDialog={onCloseDialog}
				defaultValue={params.value}
				onConfirm={onConfirm}
			/>
		</>
	);
};

export default TransferAmountCell;
